import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { CoreCOMService } from '../../services/com.service';
import { CoreLogService } from '../../services/log.service';
import { snackBarMessageKey, SnackBarService } from '../../services/snackBar.service';
import { SnackBarMessageType } from '../../shared/contentTypes';
import { CustomerInputFieldViewModel } from '../../shared/models';
import { UserAddressViewModel } from '../../shared/models/userInfo.model';
import { OverlaySpinnerService } from '../overlayspinner/overlaySpiner.service';
import { UserProfileService } from '../userProfile/userprofile.service';
import { BillingInfoService } from './billingInfo.service';
import { DynamicFormFieldsComponent } from '../../directives';

@Component({
  selector: 'ya-billinginfo',
  templateUrl: './billingInfo.component.html',
  styleUrls: ['./billingInfo.component.scss'],
  providers: [OverlaySpinnerService, UserProfileService, BillingInfoService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BillingInfoComponent implements OnInit, OnDestroy, AfterViewInit {
  public userAddressViewModel: UserAddressViewModel = new UserAddressViewModel();
  public billingInfoInputsViewModel: CustomerInputFieldViewModel[];

  public isOnline: boolean;

  private _langSubscriber: Subscription;
  private _onlineSupscription: Subscription;
  private _offlineSupscription: Subscription;

  @ViewChild("dynamicFormFieldsComponent") dynamicFormFieldsComponent: DynamicFormFieldsComponent;

  constructor(private _billingInfoService: BillingInfoService
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreCOMService: CoreCOMService
    , private _internationalisationService: InternationalisationService
    , private _snackBarService: SnackBarService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _coreLogService: CoreLogService) { }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("BillingInfoComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngAfterViewInit(): void { }

  ngOnInit() {
    const me = this;
    me.reloadDataAndRender();

    me.isOnline = me._coreCOMService.environment.networkInformation.isOnline;
    me._langSubscriber = me._internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me._onlineSupscription = me._coreCOMService.environment.events.online.subscribe(() => {
      me.isOnline = true;
      me.PushChanges();
    });

    me._offlineSupscription = me._coreCOMService.environment.events.offline.subscribe(() => {
      me.isOnline = false;
      me.PushChanges();
    });

    me._billingInfoService.webshopSettingsDataModelObserveChanges.subscribe(changes => {
      me.reloadDataAndRender();
    });
  }

  private reloadDataAndRender() {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._billingInfoService.getAddressInputFields()
      .then(addressinputFields => {
        me.billingInfoInputsViewModel = addressinputFields;
        return me.GetBillingInfo();
      })
      .then(() => {
        me.PushChanges();
        me._overlaySpinnerService.HideLayoutSpinner();
      })
      .catch(error => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._coreLogService.error("BillingInfoComponent.reloadDataAndRender", "_billingInfoService.getAddressInputFields", error);
      })
  }

  public saveUserInfo() {
    const me = this;

    if (me.hasDynamicFormGroup) {
      const formData = me.dynamicFormFieldsComponent.getDynamicFieldsInput();
      Object.assign(me.userAddressViewModel, formData);
    }

    me._overlaySpinnerService.ShowLayoutSpinner();
    me._billingInfoService.saveCustomerMainAddressDataModel(me.userAddressViewModel)
      .then(() => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._snackBarService.ShowMessage(snackBarMessageKey.successfullyExecuted, SnackBarMessageType.Successful);
        me.PushChanges();
      })
      .catch(error => {
        me._coreLogService.error("BillingInfoComponent.saveUserInfo", "_billingInfoService.saveCustomerMainAddressDataModel", error)
        me._snackBarService.ShowErrorMessageFailed(2);
        me._overlaySpinnerService.HideLayoutSpinner();
      })
  }

  /**get userInfo base webshop billing info */
  private GetBillingInfo(): Promise<void> {
    const me = this;
    return me._billingInfoService.GetUserBillingInfo()
      .then(billingInfo => {
        me.userAddressViewModel = billingInfo;
      })
      .catch(e => {
        me._coreLogService.error("BillingInfoComponent.GetBillingInfo", "_billingInfoService.GetUserBillingInfo", e);
      });
  }

  /** Returns the initial user address data used to fill the form.*/
  public getUserInfoInitialData() {
    const me = this;
    return me.userAddressViewModel;
  }
  /**
 * Triggered whenever the form status changes.
 * Propagates the updated state to any listeners or parent components.
 */
  public onFormStatusChange(): void {
    const me = this;
    me.PushChanges();
  }

  /** Indicates whether the dynamic form group exists.*/
  private get hasDynamicFormGroup(): boolean {
    const me = this;
    return !!(me.dynamicFormFieldsComponent && me.dynamicFormFieldsComponent.formGroup);
  }

  /** Checks whether the dynamic form group is present and currently valid.*/
  public isDynamicFormGroupValid(): boolean {
    const me = this;
    return me.hasDynamicFormGroup && me.dynamicFormFieldsComponent.formGroup.valid;
  }

  /** Marks all controls within the dynamic form group as touched. */
  public markAllFieldsAsTouched(): void {
    const me = this;
    if (me.dynamicFormFieldsComponent) {
      me.dynamicFormFieldsComponent.markAllFieldsAsTouched();
    }
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    }
    if (me._onlineSupscription) {
      me._onlineSupscription.unsubscribe();
    }
    if (me._offlineSupscription) {
      me._offlineSupscription.unsubscribe();
    }
  }
}
